import { useState, useEffect } from 'react';
import { testimonialAPI, Testimonial } from '../services/api';

export const useTestimonials = (onlyApproved = true) => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState<number>(0); // Add a refresh key

  // Function to trigger data refresh
  const refreshData = () => {
    setRefreshKey(prev => prev + 1);
  };

  useEffect(() => {
    const fetchTestimonials = async () => {
      setLoading(true);
      setError(null); // Clear previous errors
      
      try {
        console.log('Fetching testimonials...');
        
        // Try to get data from the API
        try {
          const response = await testimonialAPI.getTestimonials(onlyApproved);
          console.log('Testimonials API response:', response);
          
          if (response && response.success && Array.isArray(response.data)) {
            // Format the testimonials data for consistency
            const apiTestimonials = response.data.map((item: any) => ({
              id: item._id || item.id,
              name: item.name,
              role: item.role,
              content: item.content,
              rating: item.rating,
              date: new Date(item.date),
              approved: item.approved
            }));
            
            setTestimonials(apiTestimonials);
            setLoading(false);
            return; // Exit early since we have API data
          } else {
            console.warn('API response did not contain valid testimonials data:', response);
          }
        } catch (apiError) {
          console.error('API error:', apiError);
        }
        
        // Fallback to mock data if API fails
        console.log('Using fallback mock data for testimonials');
        const mockTestimonials = [
          {
            id: '67f7200a3a2f55e2b11dc6ec', // Use real MongoDB IDs from the seeded data
            name: 'Jane Smith',
            role: 'Business Owner',
            content: 'The mural completely transformed our office space. Everyone who visits is impressed by the quality and creativity.',
            rating: 5,
            date: new Date('2023-11-15'),
            approved: true
          },
          {
            id: '67f7200a3a2f55e2b11dc6ed',
            name: 'Mark Johnson',
            role: 'Community Center Director',
            content: 'Working with this artist was a pleasure from start to finish. The mural exceeded our expectations and has become a focal point for our community.',
            rating: 5,
            date: new Date('2023-10-22'),
            approved: true
          },
          {
            id: '67f7200a3a2f55e2b11dc6ee',
            name: 'Sarah Williams',
            role: 'School Principal',
            content: 'Our students are inspired every day by the beautiful artwork. The artist was professional, timely, and worked within our budget.',
            rating: 4,
            date: new Date('2023-09-10'),
            approved: true
          },
          {
            id: '67f7200a3a2f55e2b11dc6ef',
            name: 'David Chen',
            role: 'Restaurant Owner',
            content: 'Customers love taking photos with our new mural. It has helped increase our social media presence significantly.',
            rating: 5,
            date: new Date('2023-12-05'),
            approved: false
          }
        ];
        
        // Filter based on onlyApproved parameter
        const filteredTestimonials = onlyApproved 
          ? mockTestimonials.filter(t => t.approved)
          : mockTestimonials;
            
        setTestimonials(filteredTestimonials);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching testimonials:', err);
        setError('Failed to load testimonials');
        setLoading(false);
      }
    };
    
    fetchTestimonials();
  }, [onlyApproved, refreshKey]); // Add refreshKey as a dependency

  const createTestimonial = async (testimonial: Testimonial) => {
    try {
      console.log('Creating testimonial:', testimonial);
      
      let newTestimonial: Testimonial = {
        ...testimonial,
        date: new Date(),
        approved: false
      };
      
      // Use the real API
      try {
        const response = await testimonialAPI.createTestimonial(testimonial);
        console.log('Create testimonial API response:', response);
        
        // Use the data from the API
        if (response && response.success && response.data) {
          newTestimonial = {
            ...response.data,
            id: response.data._id || response.data.id, // Use MongoDB _id
            date: new Date(response.data.date),
            approved: response.data.approved || false
          };
        }
        
        // Add to our local state
        setTestimonials(prev => [...prev, newTestimonial]);
        
        refreshData(); // Refresh data after creation
        return { 
          success: true,
          data: newTestimonial
        };
      } catch (apiErr) {
        console.error('API error when creating testimonial:', apiErr);
        return { 
          success: false, 
          error: 'Failed to create testimonial' 
        };
      }
    } catch (err) {
      console.error('Error creating testimonial:', err);
      return { 
        success: false, 
        error: 'Failed to create testimonial' 
      };
    }
  };

  const updateTestimonial = async (id: string, data: Partial<Testimonial>) => {
    try {
      console.log('Updating testimonial with ID:', id);
      console.log('Update data:', data);
      
      // Make the real API call
      try {
        const response = await testimonialAPI.updateTestimonial(id, data);
        console.log('Update testimonial response:', response);
        
        if (response && response.success && response.data) {
          // Update with the data from the API
          const updatedTestimonial = {
            ...response.data,
            id: response.data._id || response.data.id,
            date: new Date(response.data.date)
          };
          
          // Update the testimonial in the list
          setTestimonials(prev => 
            prev.map(t => t.id === id ? updatedTestimonial : t)
          );
        } else {
          // Fallback to local update if API response is invalid
          setTestimonials(prev => 
            prev.map(t => t.id === id ? { ...t, ...data } : t)
          );
        }
      } catch (apiErr) {
        console.error('API error when updating testimonial:', apiErr);
        // Still update local state even if API fails
        setTestimonials(prev => 
          prev.map(t => t.id === id ? { ...t, ...data } : t)
        );
      }
      
      refreshData(); // Refresh data after update
      return { success: true };
    } catch (err) {
      console.error('Error updating testimonial:', err);
      return { 
        success: false, 
        error: 'Failed to update testimonial' 
      };
    }
  };

  const deleteTestimonial = async (id: string) => {
    try {
      console.log('Deleting testimonial with ID:', id);
      
      // Make the real API call first
      try {
        const response = await testimonialAPI.deleteTestimonial(id);
        console.log('Delete testimonial response:', response);
        
        if (response && response.success) {
          // Filter out the deleted testimonial
          setTestimonials(prev => prev.filter(t => t.id !== id));
          refreshData(); // Refresh data after deletion
          return { success: true };
        }
      } catch (apiErr) {
        console.error('API error when deleting testimonial:', apiErr);
      }
      
      // Fallback: still update local state even if API fails
      setTestimonials(prev => prev.filter(t => t.id !== id));
      refreshData(); // Refresh data after deletion
      return { success: true };
    } catch (err) {
      console.error('Error deleting testimonial:', err);
      return { 
        success: false, 
        error: 'Failed to delete testimonial' 
      };
    }
  };

  const approveTestimonial = async (id: string) => {
    try {
      console.log('Approving testimonial with ID:', id);
      
      // Try the real API call first
      try {
        console.log(`Attempting API call to approve testimonial: ${id}`);
        const response = await testimonialAPI.approveTestimonial(id);
        console.log('Approve testimonial API response:', response);
        
        if (response && response.success && response.data) {
          // Use the response data to update our state
          const approvedTestimonial = {
            ...response.data,
            id: response.data._id || response.data.id,
            date: new Date(response.data.date),
            approved: true
          };
          
          setTestimonials(prev => 
            prev.map(t => t.id === id ? approvedTestimonial : t)
          );
          
          refreshData();
          return { success: true };
        }
      } catch (apiErr) {
        console.error('API error when approving testimonial:', apiErr);
      }
      
      // Fallback to local state update if API fails
      setTestimonials(prev => 
        prev.map(t => t.id === id ? { ...t, approved: true } : t)
      );
      
      refreshData();
      return { success: true };
    } catch (err) {
      console.error('Error approving testimonial:', err);
      return { 
        success: false, 
        error: 'Failed to approve testimonial' 
      };
    }
  };

  return { 
    testimonials, 
    loading, 
    error,
    createTestimonial,
    updateTestimonial,
    deleteTestimonial,
    approveTestimonial,
    refreshData
  };
};

export default useTestimonials;